import {
  BenefitDto,
  CarrierPaymentMethod,
  EmployeeDto,
  EmployeePaymentMethodType,
  PaymentsProvider,
} from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { useImpersonation, useMonolithQuery } from '@zorro/web/ui-utils';

import { derivePaymentMethodTypeFromBenefit } from '../PaymentMethodUtils';
import { getPaymentHandlerRole } from '../PaymentStatus/PaymentStatusUtils';
import { PaymentNoteInfoProps } from '../PaymentStatus/paymentNoteText.utils';
import { useLynxPaymentsContext } from '../ZorroPay/hooks/useLynxPaymentsContext';
import { PaymentMethodToDisplay } from '../ZorroPay/paymentMethodsToDisplay.utils';
import {
  PaymentMethodViewer,
  getMethodDisplaySpecs,
} from './paymentMethodDisplayRules';

type UsePaymentMethodDisplayDataProps = {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  viewer: PaymentMethodViewer;
  /**
   * Editing flows pass the in-form type so a benefit stored as self-pay
   * fetches methods once the selector moves off self-pay (and vice versa).
   */
  formPaymentMethodType?: EmployeePaymentMethodType;
};

// Every surface reveals through the /card app; ops reveals someone else's
// card, so it addresses the owner explicitly.
function buildCardRevealUrl(
  viewer: PaymentMethodViewer,
  employee: EmployeeDto | undefined
): string | undefined {
  const url = new URL(APP_ROUTES.CARD(), process.env.NEXT_PUBLIC_APP_BASE_URL);
  if (viewer === PaymentMethodViewer.EMPLOYEE) {
    return url.toString();
  }
  if (!employee) {
    return undefined;
  }
  url.searchParams.set('cardOwnerEmployeeUserId', employee.userId);
  url.searchParams.set('cardOwnerEmployerId', employee.employerId);
  return url.toString();
}

type LynxMethodsDataProps = {
  employeeId: string;
  onboardingPeriodId: string;
  viewer: PaymentMethodViewer;
  /** False until the provider resolved to Lynx AND the account is active. */
  enabled: boolean;
  majorMedicalBenefit: BenefitDto | null | undefined;
};

function useLynxMethodsData({
  employeeId,
  onboardingPeriodId,
  viewer,
  enabled,
  majorMedicalBenefit,
}: LynxMethodsDataProps) {
  const { isImpersonated } = useImpersonation();

  const { data: employee, isLoading: isEmployeeLoading } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: enabled ? [employeeId] : undefined,
  });

  const carrierName = majorMedicalBenefit?.plan?.carrierName;
  const employeeState = employee?.state;
  const paymentMethodOverride = majorMedicalBenefit?.paymentMethodOverride;

  const shouldFetchCarrier =
    enabled && !paymentMethodOverride && !!employeeState && !!carrierName;
  const { data: carrier, isLoading: isCarrierLoading } = useMonolithQuery({
    method: 'majorMedicalCarriersControllerFindByNameAndState',
    params: shouldFetchCarrier ? [employeeState, carrierName] : undefined,
  });

  const carrierPaymentMethod =
    paymentMethodOverride ??
    carrier?.paymentMethod ??
    CarrierPaymentMethod.CARD;

  const handlerRole = getPaymentHandlerRole(
    majorMedicalBenefit?.initialPremiumPaymentStatus,
    majorMedicalBenefit?.autoPayStatus
  );

  const methodSpecs = getMethodDisplaySpecs({
    viewer,
    carrierPaymentMethod,
    handlerRole,
    isImpersonated,
  });

  const shouldFetchCards =
    enabled &&
    methodSpecs.some(({ method }) => method !== PaymentMethodToDisplay.ACH);
  const { data: employeeCards, isLoading: isCardsLoading } = useMonolithQuery({
    method: 'paymentsControllerGetEmployeeCards',
    params: shouldFetchCards ? [employeeId] : undefined,
  });

  const shouldFetchEnrollmentTeam =
    enabled && methodSpecs.some(({ showWhoPaysNote }) => showWhoPaysNote);
  const { data: onboardingPeriodReport, isLoading: isEnrollmentTeamLoading } =
    useMonolithQuery({
      method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
      params: shouldFetchEnrollmentTeam ? [onboardingPeriodId] : undefined,
    });

  // Toggles stay locked until the employee's first allocation succeeds.
  const shouldFetchAllocation = enabled && !!majorMedicalBenefit;
  const { data: isFirstAllocationSucceeded, isError: isAllocationError } =
    useMonolithQuery({
      method: 'paymentsControllerGetIsFirstAllocationSucceeded',
      params: shouldFetchAllocation
        ? [employeeId, majorMedicalBenefit.id]
        : undefined,
    });

  const opsCard = employeeCards?.opsCard ?? null;

  // The per-query isLoading flags report false while their params are still
  // undefined, so alone they'd miss the gap right after an upstream query
  // resolves but before the dependent one starts fetching.
  const isEmployeePending = enabled && employee === undefined;
  const isCarrierPending = shouldFetchCarrier && carrier === undefined;
  const isCardsPending = shouldFetchCards && employeeCards === undefined;
  const isAllocationPending =
    shouldFetchAllocation &&
    isFirstAllocationSucceeded === undefined &&
    !isAllocationError;

  const isMethodsLoading =
    isEmployeeLoading ||
    isCarrierLoading ||
    isCardsLoading ||
    isEmployeePending ||
    isCarrierPending ||
    isCardsPending ||
    isAllocationPending;

  const billingAddress = [employee?.address, employee?.zipCode, employee?.state]
    .filter(Boolean)
    .join(', ');

  const paymentNoteInfo: PaymentNoteInfoProps = {
    paymentHandlerRole: handlerRole,
    enrollmentTeamName: onboardingPeriodReport?.enrollmentTeamName,
    opsCardLastFour: opsCard?.cardLastFour,
  };

  return {
    methodSpecs,
    employee,
    employeeCard: employeeCards?.employeeCard ?? null,
    opsCard,
    billingAddress,
    paymentNoteInfo,
    isWhoPaysNoteReady: !isMethodsLoading && !isEnrollmentTeamLoading,
    isMethodsLoading,
    isImpersonated,
    isFirstAllocationSucceeded: isFirstAllocationSucceeded ?? false,
  };
}

export function usePaymentMethodDisplayData({
  employeeId,
  employerId,
  onboardingPeriodId,
  viewer,
  formPaymentMethodType,
}: UsePaymentMethodDisplayDataProps) {
  const { isLynxEnabled, paymentsProvider, isAccountActive } =
    useLynxPaymentsContext({ employerId, employeeId });

  const isLynx = paymentsProvider === PaymentsProvider.LYNX;
  const isEcho = paymentsProvider === PaymentsProvider.ECHO;

  // Echo reads the payment method type off the details endpoint
  const { data: echoDetails, isLoading: isEchoDetailsLoading } =
    useMonolithQuery({
      method: 'paymentsControllerEmployeePaymentMethodDetails',
      params: isEcho ? [employeeId, { onboardingPeriodId }] : undefined,
    });

  // Lynx derives it from the major medical benefit, which also carries the
  // payment statuses (handler role) and the carrier name (ACH vs card)
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: isLynx ? [onboardingPeriodId] : undefined,
  });
  const majorMedicalBenefit = benefits?.majorMedicalBenefit;
  const majorMedicalPlan = majorMedicalBenefit?.plan;

  const lynxPaymentMethodType =
    majorMedicalPlan && derivePaymentMethodTypeFromBenefit(majorMedicalPlan);
  const isSelfPay =
    (formPaymentMethodType ?? lynxPaymentMethodType) ===
    EmployeePaymentMethodType.SELF_PAY;

  const lynxMethodsData = useLynxMethodsData({
    employeeId,
    onboardingPeriodId,
    viewer,
    // The method list waits for an active Lynx account; the payment method
    // type (self-pay notice, amount distribution) intentionally doesn't.
    enabled: isLynx && isAccountActive === true && !isSelfPay,
    majorMedicalBenefit,
  });

  // Echo employees without generated details fall back to Zorro Pay — the
  // Echo details component renders its own "not available" state for them.
  const echoPaymentMethodType = isEchoDetailsLoading
    ? undefined
    : (echoDetails?.type ?? EmployeePaymentMethodType.ZORRO_PAY);
  const paymentMethodType = isLynx
    ? lynxPaymentMethodType
    : echoPaymentMethodType;

  const isTypeLoading =
    paymentsProvider === undefined || (isLynx && benefits === undefined);

  const revealUrl = buildCardRevealUrl(viewer, lynxMethodsData.employee);
  const openCardReveal = () => {
    if (revealUrl) {
      window.open(revealUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return {
    ...lynxMethodsData,
    isLynxEnabled,
    paymentsProvider,
    isAccountActive,
    paymentMethodType,
    isTypeLoading,
    openCardReveal,
  };
}
